import React, { useContext } from "react";
import { snakeify } from "./../utils/caseConvert";
import { useAuth0 } from "./react-auth0-spa";
import axios from "axios";
import { parseCSVLine } from "../lib/helpers";
import { activeOrgHeader } from "../utils/activeOrg";

const baseUrlSec =
  process.env.REACT_APP_ENV !== "development" ? "https://" : "http://";
const baseURL = process.env.REACT_APP_BACKEND_ENDPOINT || "localhost:8000";
const ACAO =
  process.env.REACT_APP_ENV !== "development"
    ? process.env.REACT_APP_ENV === "staging"
      ? "https://mos-id-backend-staging.vectech.io"
      : "https://mos-id-backend.vectech.io"
    : "*";

const apiConfig = {
  mainAPI: {
    baseURL: baseUrlSec + baseURL,
  },
};

// The organization service is no longer addressed differently from any other
// backend service.
//
// It used to have its own baseURL from REACT_APP_ORGANIZATION_API - a
// per-environment API Gateway id baked into the build. CloudFormation mints
// that id, so it changed whenever the stack was recreated, and it did when the
// service moved into the staging account, silently pointing a rebuilt frontend
// at the old one. It is now reached on the same host as everything else,
// through the load balancer's /organization* and /algorithm* rules.
//
// What is left is only the local dev port - the service runs as its own
// process on 50057, because there is no load balancer locally to route by
// path - and that is what `devPort` already expresses for userservice (5004)
// and dataservice (5005). So org uses the same mechanism and the same
// spelling.
//
// sendOrganizationRequest still exists, and is about something else entirely:
// the service reads camelCase query params and body keys. See below.

const WebServerContext = React.createContext();
const useWebServer = () => useContext(WebServerContext);

const WebServerProvider = ({ children }) => {
  const { getTokenSilently } = useAuth0();

  const getToken = async (ignoreCache = false) => {
    try {
      return await getTokenSilently({ ignoreCache: ignoreCache });
    } catch (e) {
      try {
        return await getTokenSilently({ ignoreCache: ignoreCache });
      } catch (e) {
        console.error("fail to get token");
      }
    }
  };

  // Every internal API instance is built here - the main backend, the
  // organization microservice, and the websocket health ping - so the org header
  // is applied once, in one place. Read at call time rather than captured, so a
  // switch takes effect on the next request.
  //
  // Deliberately NOT applied to the two axios.get calls below: those go to
  // nominatim.openstreetmap.org, and an internal header has no business being
  // sent to a third party.
  const createApi = ({ baseURL, token }) =>
    axios.create({
      baseURL,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": ACAO,
        ...activeOrgHeader(),
      },
    });

  const createTokenizedAPI = async ({ devPort }) => {
    const token = await getToken();
    return createApi({
      baseURL:
        devPort && process.env.REACT_APP_ENV === "development"
          ? String(apiConfig.mainAPI.baseURL).slice(0, -4) + devPort
          : apiConfig.mainAPI.baseURL,
      token,
    });
  };

  // `signal` (an AbortSignal) is opt-in per request and cancels it when aborted,
  // including while it is still waiting on the auth token: axios then rejects it
  // without sending it. axios has no default timeout, so a request that stalls in
  // the browser or on a dead connection otherwise never settles - no response
  // and no error. There is deliberately no global timeout: some callers (report
  // downloads, organization data) legitimately run long.
  const sendRequest =
    (method) =>
    async ({ url, data, params, devPort = null, signal }) => {
      const api = await createTokenizedAPI({ devPort });
      const snakedParams = snakeify(params, true);
      const response = await api.request({
        url,
        method,
        data: snakeify(data) || {},
        params: snakedParams,
        signal,
      });
      return response;
    };

  // NOT about where the organization service is - that is the same host as
  // everything else now. This exists because the service reads camelCase query
  // params and body keys, so the snakeify() that sendRequest applies would
  // rename them, and would also mangle the opaque pagination cursor.
  //
  // Only some of its endpoints want this. The older ones are still called
  // through sendRequest with devPort: 50057 and do take snake_case. Collapsing
  // the two would need the service to settle on one convention first.
  const sendOrganizationRequest =
    (method) =>
    async ({ url, data, params }) => {
      const api = await createTokenizedAPI({ devPort: 50057 });
      return api.request({ url, method, params, data });
    };

  const getGPSLocationFromAddress = async (address) => {
    const resp = await axios.get(
      `https://nominatim.openstreetmap.org/search?q='+${address}&format=jsonv2&addressdetails=1`,
    );
    return resp;
  };

  const getAddressFromGPSLocation = async (lat, lng) => {
    try {
      const resp = await axios.get(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&accept-language=en`,
      );
      return resp;
    } catch (err) {
      console.log(err);
    }
  };

  const wsReconnectPingServer = async ({ timeout = 0 }) => {
    const token = await getToken();
    const api = createApi({
      baseURL: getWebsocketBaseUrl(),
      token,
    });
    return api.get("/health", { timeout });
  };

  const getWebsocketUrl = () => {
    // const protocol = baseUrlSec === 'https://' ? 'wss://' : 'ws://';
    if (process.env.REACT_APP_ENV === "development") {
      return `${baseUrlSec}localhost:8889`;
    }
    return `${baseUrlSec}${baseURL}`;
  };

  const getWebsocketBaseUrl = () => {
    if (process.env.REACT_APP_ENV === "development") {
      return `${baseUrlSec}localhost:8889`;
    }
    return `${baseUrlSec}${baseURL}`;
  };

  const downloadOrganizationData = async ({ filters, isDownload = false }) => {
    const api = await createTokenizedAPI({ devPort: 50057 });
    const res = await api.request({
      url: `/organization/data`,
      method: "post",
      data: filters || {},
      isDownload,
      responseType: "json",
    });
    return res;
  };

  const downloadOrganizationDataWithRawData = async ({
    filters,
    isDownload = false,
  }) => {
    const api = await createTokenizedAPI({ devPort: 50057 });
    const res = await api.request({
      url: `/organization/data`,
      method: "post",
      data: filters || {},
      responseType: "json",
    });
    try {
      const response = await fetch(res?.data?.url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const csvText = await response.text();

      // Parse CSV data
      const rows = [];
      const lines = csvText.split("\n").filter((line) => line.trim() !== "");

      if (lines.length === 0) {
        return { data: [] };
      }

      // headers
      const headers = lines[0]
        .split(",")
        .map((header) => header.trim().replace(/"/g, ""));

      // Process each data row (skip header row)
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        const values = parseCSVLine(line);
        const rowObject = {};

        headers.forEach((header, index) => {
          rowObject[header] = values[index] || "";
        });

        rows.push(rowObject);
      }

      return { data: rows };
    } catch (error) {
      console.error("Error fetching JSON from S3:", error);
    }
  };





  const downloadMonthlyImaging = async ({ filters }) => {
    const api = await createTokenizedAPI({ devPort: 50057 });
    const res = await api.request({
      url: `/organization/data/monthlyImaging`,
      method: "post",
      data: filters || {},
      responseType: "json",
    });
    return res;
  };

  const downloadWeeklyImaging = async ({ filters }) => {
    const api = await createTokenizedAPI({ devPort: 50057 });
    const res = await api.request({
      url: `/organization/data/weeklyImaging`,
      method: "post",
      data: filters || {},
      responseType: "json",
    });
    return res;
  };

  return (
    <WebServerContext.Provider
      value={{
        sendRequest,
        sendOrganizationRequest,
        getGPSLocationFromAddress,
        getAddressFromGPSLocation,
        getWebsocketUrl,
        wsReconnectPingServer,
        getToken,
        downloadOrganizationData,
        downloadOrganizationDataWithRawData,
        downloadMonthlyImaging,
        downloadWeeklyImaging,
      }}
    >
      {children}
    </WebServerContext.Provider>
  );
};

export default WebServerProvider;
export { useWebServer };
